import React, { useState, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Skeleton,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Grid,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Undo as RevertIcon,
  AccessTime as TimeIcon,
  Person as PersonIcon,
  MenuBook as SubjectIcon,
  Room as RoomIcon,
  SwapHoriz as ConvertIcon,
  Science as LabIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { addDays, startOfDay, subYears } from "date-fns";
import { DATE_DISPLAY_FORMAT, toApiDate } from "../../utils/dateUtils";
import editLectureService from "../../services/editLectureService";
import conversionService from "../../services/conversionService";
import { usePermissions } from "../../hooks/usePermissions";
import {
  EDIT_LECTURE_OVERRIDE,
  EDIT_LECTURE_OWN_OVERRIDE,
} from "../../utils/editLecturePermissions";
import {
  TIMETABLE_CONVERT,
  TIMETABLE_CONVERT_OWN,
} from "../../utils/timetablePermissions";
import EditLectureForm from "./EditLectureForm";
import ConvertLabToTheoryModal from "./ConvertLabToTheoryModal";
import ConvertTheoryToLabModal from "./ConvertTheoryToLabModal";

/**
 * Get current day name (matches TimeTableSlot.dayOfWeek format).
 */
const DAY_NAMES = ["SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY"];
const getCurrentDayName = () => DAY_NAMES[new Date().getDay()];
/** Weekday name (SUNDAY..SATURDAY) for a given Date, used to tag slots for the picked day. */
const getDayNameForDate = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  return Number.isNaN(d.getTime()) ? getCurrentDayName() : DAY_NAMES[d.getDay()];
};

/**
 * Override status chip color map. Converted takes visual priority since a
 * converted slot is a virtual injected slot (isConverted === true) and never
 * carries edit/swap/extend flags.
 */
const getOverrideChipProps = (slot) => {
  if (!slot) return null;
  if (slot.isConverted) return { label: "Converted", color: "secondary" };
  if (slot.isCancelledByExtend) return { label: "Cancelled", color: "error" };
  if (slot.isEdited) return { label: "Edited", color: "secondary" };
  if (slot.isSwapped) return { label: "Swapped", color: "info" };
  if (slot.isExtended) return { label: "Extended", color: "warning" };
  return null;
};

// ---------------------------------------------------------------------------
// Conversion detection helpers (Task 14)
// ---------------------------------------------------------------------------

/** Normalize a raw slot time ("HH:MM" | "HH:MM:SS" | Date-ish) to "HH:MM". */
const normalizeSlotTime = (value) => {
  if (value == null) return "";
  const m = String(value).trim().match(/^(\d{1,2}):(\d{2})/);
  if (!m) return "";
  return `${String(Number(m[1])).padStart(2, "0")}:${m[2]}`;
};

/** True when a slot represents a lab/practical batch slot. */
const isLabSlot = (slot) => {
  if (!slot || slot.isBreak) return false;
  const lt = String(slot.lectureType || "").toUpperCase();
  return lt === "PRACTICAL" || lt === "LAB" || slot.practicalBatchCode != null;
};

/**
 * True when a slot is an eligible THEORY slot for theory→lab conversion.
 * Excludes breaks, labs, and already-converted virtual slots.
 */
const isEligibleTheorySlot = (slot) => {
  if (!slot || slot.isBreak || slot.isConverted) return false;
  if (slot.practicalBatchCode != null) return false;
  const lt = String(slot.lectureType || "").toUpperCase();
  // Treat explicit THEORY, or "not a lab type", as theory. Must still have teacher/subject.
  const notLab = lt !== "PRACTICAL" && lt !== "LAB";
  const isTheory = lt === "THEORY" || (notLab && lt !== "");
  const looksTheory = isTheory || lt === "";
  return looksTheory && (slot.teacher?.id != null || slot.subject?.id != null);
};

/**
 * Group a classGroup's slots into LAB BLOCK GROUPS: lab slots sharing the same
 * startTime + endTime (parallel batches). A single-batch lab also qualifies as
 * its own block. Returns an array of { key, slots } sorted by startTime.
 * Already-converted virtual lab slots are excluded (they are not re-convertible).
 */
const detectLabBlocks = (slots) => {
  if (!Array.isArray(slots)) return [];
  const map = new Map();
  for (const slot of slots) {
    if (!slot || slot.isConverted) continue;
    if (!isLabSlot(slot)) continue;
    const start = normalizeSlotTime(slot.startTime);
    const end = normalizeSlotTime(slot.endTime);
    if (!start || !end) continue;
    const key = `${start}|${end}`;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(slot);
  }
  return Array.from(map.entries())
    .map(([key, blockSlots]) => ({ key, slots: blockSlots }))
    .sort((a, b) => String(a.key).localeCompare(String(b.key)));
};

/**
 * Group adjacent eligible THEORY slots (sorted by startTime, where
 * slot[n].endTime === slot[n+1].startTime) into consecutive chains within a
 * class. Returns an array of chains (each an array of slot objects, length >= 1).
 * Breaks/labs/converted slots act as chain separators.
 */
const detectConsecutiveTheoryChains = (slots) => {
  if (!Array.isArray(slots)) return [];
  const theory = slots
    .filter(isEligibleTheorySlot)
    .slice()
    .sort((a, b) => {
      const av = normalizeSlotTime(a.startTime);
      const bv = normalizeSlotTime(b.startTime);
      return String(av).localeCompare(String(bv));
    });

  const chains = [];
  let current = [];
  for (let i = 0; i < theory.length; i++) {
    const slot = theory[i];
    if (current.length === 0) {
      current = [slot];
      continue;
    }
    const prevEnd = normalizeSlotTime(current[current.length - 1].endTime);
    const thisStart = normalizeSlotTime(slot.startTime);
    if (prevEnd && thisStart && prevEnd === thisStart) {
      current.push(slot);
    } else {
      chains.push(current);
      current = [slot];
    }
  }
  if (current.length > 0) chains.push(current);
  return chains;
};

/**
 * Format time string (HH:mm:ss or HH:mm) to 12h display.
 */
const formatTime12h = (time) => {
  if (!time) return "";
  const [h, m] = time.split(":");
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${m} ${ampm}`;
};

/**
 * Skeleton for loading state — mimics schedule card layout.
 */
const ScheduleSkeleton = () => (
  <Box>
    <Skeleton variant="rounded" width={200} height={40} sx={{ mb: 2 }} />
    {[1, 2, 3].map((i) => (
      <Card key={i} sx={{ mb: 2 }}>
        <CardContent>
          <Skeleton variant="text" width="30%" height={28} sx={{ mb: 1 }} />
          {[1, 2, 3].map((j) => (
            <Box key={j} sx={{ display: "flex", gap: 2, mb: 1.5 }}>
              <Skeleton variant="rounded" width="100%" height={72} />
            </Box>
          ))}
        </CardContent>
      </Card>
    ))}
  </Box>
);

/**
 * Slot detail dialog — shows original vs override values for edited slots.
 */
const SlotDetailDialog = ({ open, onClose, slot }) => {
  if (!slot) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Slot Details</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Time
          </Typography>
          <Typography>
            {formatTime12h(slot.startTime)} – {formatTime12h(slot.endTime)}
          </Typography>
        </Box>
        <Divider sx={{ my: 1.5 }} />
        {slot.isEdited && (
          <>
            <Typography variant="subtitle2" color="secondary" sx={{ mb: 1 }}>
              Override Values (Current)
            </Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Teacher
                </Typography>
                <Typography variant="body2">
                  {slot.teacher?.name || "—"}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Subject
                </Typography>
                <Typography variant="body2">
                  {slot.subject?.name || "—"}
                </Typography>
              </Grid>
            </Grid>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Original Values
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Teacher
                </Typography>
                <Typography variant="body2">
                  {slot.originalTeacher?.name || slot.teacher?.name || "—"}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Subject
                </Typography>
                <Typography variant="body2">
                  {slot.originalSubject?.name || slot.subject?.name || "—"}
                </Typography>
              </Grid>
            </Grid>
          </>
        )}
        {!slot.isEdited && (
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                Teacher
              </Typography>
              <Typography variant="body2">
                {slot.teacher?.name || "—"}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                Subject
              </Typography>
              <Typography variant="body2">
                {slot.subject?.name || "—"}
              </Typography>
            </Grid>
          </Grid>
        )}
        <Divider sx={{ my: 1.5 }} />
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Room
            </Typography>
            <Typography variant="body2">{slot.room || "—"}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Type
            </Typography>
            <Typography variant="body2" sx={{ textTransform: "capitalize" }}>
              {slot.lectureType || "—"}
            </Typography>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const DepartmentSchedule = () => {
  const { hasVisiblePermission } = usePermissions();
  const { user } = useSelector((state) => state.auth);
  const queryClient = useQueryClient();

  const canOverride = hasVisiblePermission(EDIT_LECTURE_OVERRIDE);
  const canOwnOverride = hasVisiblePermission(EDIT_LECTURE_OWN_OVERRIDE);
  // Conversion permissions: `convert` grants any-slot; `convert_own` grants only
  // slots the current teacher teaches (backend is the hard gate via 403).
  const canConvert = hasVisiblePermission(TIMETABLE_CONVERT);
  const canConvertOwn = hasVisiblePermission(TIMETABLE_CONVERT_OWN);
  const canConvertAny = canConvert || canConvertOwn;
  const currentTeacherId = user?.teacherId;
  const userRole = user?.role?.slug || user?.role || "";

  // Students should never see edit/delete actions
  const isStudent = userRole.toLowerCase() === "student";
  const isSuperAdmin = userRole.toLowerCase() === "superadmin" || userRole.toLowerCase() === "super_admin";

  const [selectedClassId, setSelectedClassId] = useState("");
  const [selectedDepartmentId, setSelectedDepartmentId] = useState("");
  // Date to view/edit. Defaults to today; past dates are allowed so staff can
  // edit/convert/revert historical lectures (gated server-side by marked attendance).
  const [selectedDate, setSelectedDate] = useState(() => startOfDay(new Date()));
  const [detailSlot, setDetailSlot] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [revertConfirmOpen, setRevertConfirmOpen] = useState(false);
  const [revertTarget, setRevertTarget] = useState(null);
  const [editFormOpen, setEditFormOpen] = useState(false);
  const [editFormSlot, setEditFormSlot] = useState(null);

  // Conversion modal state
  const [labToTheoryOpen, setLabToTheoryOpen] = useState(false);
  const [labToTheorySources, setLabToTheorySources] = useState([]);
  const [theoryToLabOpen, setTheoryToLabOpen] = useState(false);
  const [theoryToLabSources, setTheoryToLabSources] = useState([]);
  const [conversionContext, setConversionContext] = useState({
    classId: null,
    className: null,
  });

  // Conversion revert confirmation state
  const [convertRevertOpen, setConvertRevertOpen] = useState(false);
  const [convertRevertTarget, setConvertRevertTarget] = useState(null);

  // API-formatted selected date (YYYY-MM-DD). Empty string when invalid → backend defaults to today.
  const apiDate = toApiDate(selectedDate);
  // Date-picker bounds: no lower bound (past allowed, 1 year back is plenty); upper
  // bound mirrors the backend forward cap (today + 7).
  const pickerMinDate = useMemo(() => subYears(startOfDay(new Date()), 1), []);
  const pickerMaxDate = useMemo(() => addDays(startOfDay(new Date()), 7), []);

  // Fetch department schedule
  const {
    data: scheduleData,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ["department-schedule", selectedClassId || "all", selectedDepartmentId || "default", apiDate || "today"],
    queryFn: async () => {
      const params = {};
      if (selectedClassId) params.classId = selectedClassId;
      if (selectedDepartmentId) params.departmentId = selectedDepartmentId;
      if (apiDate) params.date = apiDate;
      const res = await editLectureService.getDepartmentSchedule(params);
      return res?.data?.data || res?.data || {};
    },
    refetchOnWindowFocus: false,
  });

  // Fetch class list independently (so the dropdown stays populated even on errors)
  const { data: classListData } = useQuery({
    queryKey: ["department-schedule-classlist", selectedDepartmentId || "default"],
    queryFn: async () => {
      const params = {};
      if (selectedDepartmentId) params.departmentId = selectedDepartmentId;
      const res = await editLectureService.getDepartmentSchedule(params);
      const data = res?.data?.data || res?.data || {};
      return data.classList || [];
    },
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // cache for 5 min
  });

  // Revert (delete) override mutation
  const revertMutation = useMutation({
    mutationFn: (overrideId) => editLectureService.deleteEditOverride(overrideId),
    onSuccess: () => {
      toast.success("Override reverted successfully");
      queryClient.invalidateQueries({ queryKey: ["department-schedule"] });
      setRevertConfirmOpen(false);
      setRevertTarget(null);
    },
    onError: (err) => {
      const msg =
        err?.response?.data?.message || err?.message || "Failed to revert override";
      toast.error(msg);
    },
  });

  // Revert (soft-delete) conversion mutation — mirrors revertMutation.
  const conversionRevertMutation = useMutation({
    mutationFn: (conversionOverrideId) =>
      conversionService.deleteConversion(conversionOverrideId),
    onSuccess: () => {
      toast.success("Conversion reverted successfully");
      queryClient.invalidateQueries({ queryKey: ["department-schedule"] });
      setConvertRevertOpen(false);
      setConvertRevertTarget(null);
    },
    onError: (err) => {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to revert conversion";
      toast.error(msg);
    },
  });

  const handleClassChange = useCallback((e) => {
    setSelectedClassId(e.target.value);
  }, []);

  const handleSlotClick = useCallback((slot) => {
    if (slot.isEdited) {
      setDetailSlot(slot);
      setDetailOpen(true);
    }
  }, []);

  const handleRevertClick = useCallback((e, slot) => {
    e.stopPropagation();
    setRevertTarget(slot);
    setRevertConfirmOpen(true);
  }, []);

  const handleRevertConfirm = useCallback(() => {
    if (revertTarget?.editOverrideId) {
      revertMutation.mutate(revertTarget.editOverrideId);
    }
  }, [revertTarget, revertMutation]);

  const handleEditClick = useCallback((e, slot, classGroup) => {
    e.stopPropagation();
    // Enrich slot with class context and day info for the form
    setEditFormSlot({
      ...slot,
      className: classGroup?.className || null,
      classId: classGroup?.classId || null,
      // Scope the teacher/subject pickers to this lecture's department. Prefer
      // an explicit department on the slot/classGroup, else the active filter.
      departmentId:
        slot?.departmentId ||
        classGroup?.departmentId ||
        selectedDepartmentId ||
        null,
      // Tag the slot with the weekday of the picked date so the form seeds/validates
      // the override date against the selected day (not today).
      dayOfWeek: slot?.dayOfWeek || getDayNameForDate(selectedDate),
    });
    setEditFormOpen(true);
  }, [selectedDepartmentId, selectedDate]);

  const handleEditFormClose = useCallback(() => {
    setEditFormOpen(false);
    setEditFormSlot(null);
  }, []);

  const handleEditFormSuccess = useCallback(() => {
    setEditFormOpen(false);
    setEditFormSlot(null);
    queryClient.invalidateQueries({ queryKey: ["department-schedule"] });
  }, [queryClient]);

  /**
   * Shape a raw department slot into the object the conversion modals expect:
   * { id, teacher:{id,name}, subject:{id,name}, practicalBatchCode, startTime,
   *   endTime, dayOfWeek }. The department view is TODAY's schedule, so fall
   *   back to today's weekday when slot.dayOfWeek is absent.
   */
  const shapeSlotForConversion = useCallback((slot) => {
    if (!slot) return null;
    return {
      id: slot.id,
      teacher: slot.teacher
        ? { id: slot.teacher.id, name: slot.teacher.name }
        : null,
      subject: slot.subject
        ? { id: slot.subject.id, name: slot.subject.name }
        : null,
      practicalBatchCode: slot.practicalBatchCode ?? null,
      startTime: slot.startTime,
      endTime: slot.endTime,
      // Fall back to the selected date's weekday (the view is date-scoped) when the
      // slot itself doesn't carry a weekday.
      dayOfWeek: slot.dayOfWeek || getDayNameForDate(selectedDate),
    };
  }, [selectedDate]);

  const handleConvertToTheory = useCallback(
    (e, blockSlots, classGroup) => {
      e.stopPropagation();
      const sources = (Array.isArray(blockSlots) ? blockSlots : [])
        .map(shapeSlotForConversion)
        .filter(Boolean);
      if (sources.length === 0) return;
      setLabToTheorySources(sources);
      setConversionContext({
        classId: classGroup?.classId || null,
        className: classGroup?.className || null,
      });
      setLabToTheoryOpen(true);
    },
    [shapeSlotForConversion]
  );

  const handleConvertToLab = useCallback(
    (e, chainSlots, classGroup) => {
      e.stopPropagation();
      const sources = (Array.isArray(chainSlots) ? chainSlots : [])
        .map(shapeSlotForConversion)
        .filter(Boolean);
      if (sources.length === 0) return;
      setTheoryToLabSources(sources);
      setConversionContext({
        classId: classGroup?.classId || null,
        className: classGroup?.className || null,
      });
      setTheoryToLabOpen(true);
    },
    [shapeSlotForConversion]
  );

  const handleConversionSuccess = useCallback(() => {
    setLabToTheoryOpen(false);
    setTheoryToLabOpen(false);
    setLabToTheorySources([]);
    setTheoryToLabSources([]);
    queryClient.invalidateQueries({ queryKey: ["department-schedule"] });
  }, [queryClient]);

  const handleConvertRevertClick = useCallback((e, slot) => {
    e.stopPropagation();
    setConvertRevertTarget(slot);
    setConvertRevertOpen(true);
  }, []);

  const handleConvertRevertConfirm = useCallback(() => {
    const overrideId = convertRevertTarget?.conversionOverrideId;
    if (overrideId != null) {
      conversionRevertMutation.mutate(overrideId);
    }
  }, [convertRevertTarget, conversionRevertMutation]);

  /**
   * Determines whether the "Edit Lecture" action should be visible for a given slot.
   * - Users with `edit_lecture.override` can edit any slot
   * - Users with only `edit_lecture.own_override` can edit only their own slots
   * - Students see no edit actions
   */
  const canEditSlot = useCallback(
    (slot) => {
      if (isStudent) return false;
      if (canOverride) return true;
      if (canOwnOverride && slot.teacher?.id && currentTeacherId) {
        return slot.teacher.id === currentTeacherId;
      }
      return false;
    },
    [isStudent, canOverride, canOwnOverride, currentTeacherId]
  );

  /**
   * Determines whether the "Revert Override" (delete) action should be visible for a given edited slot.
   * - Users with `edit_lecture.override` can revert any override
   * - Users with only `edit_lecture.own_override` can revert overrides on their own slots
   *   (i.e., where the original teacher is the current user)
   * - Students see no revert actions
   */
  const canRevertSlot = useCallback(
    (slot) => {
      if (isStudent) return false;
      if (!slot.isEdited || !slot.editOverrideId) return false;
      if (canOverride) return true;
      if (canOwnOverride && currentTeacherId) {
        // own_override allows reverting overrides on slots originally assigned to the teacher
        // The slot.teacher could be the override teacher, so we check originalTeacher if available
        const originalTeacherId = slot.originalTeacher?.id || slot.teacher?.id;
        return originalTeacherId === currentTeacherId;
      }
      return false;
    },
    [isStudent, canOverride, canOwnOverride, currentTeacherId]
  );

  /**
   * Whether the "Convert to Theory" action should show on a detected lab block.
   * - Students: never.
   * - `timetable.convert`: any lab block.
   * - `timetable.convert_own` only: only when the current teacher teaches >= 1
   *   batch in the block (backend still enforces via 403).
   */
  const canConvertLabBlock = useCallback(
    (blockSlots) => {
      if (isStudent || !canConvertAny) return false;
      if (canConvert) return true;
      if (canConvertOwn && currentTeacherId) {
        return (Array.isArray(blockSlots) ? blockSlots : []).some(
          (s) => s?.teacher?.id != null && s.teacher.id === currentTeacherId
        );
      }
      return false;
    },
    [isStudent, canConvertAny, canConvert, canConvertOwn, currentTeacherId]
  );

  /**
   * Whether the "Convert to Lab" action should show on a theory slot/chain.
   * - Students: never.
   * - `timetable.convert`: any theory slot.
   * - `timetable.convert_own` only: only theory slots the current teacher teaches.
   */
  const canConvertTheory = useCallback(
    (chainSlots) => {
      if (isStudent || !canConvertAny) return false;
      if (canConvert) return true;
      if (canConvertOwn && currentTeacherId) {
        return (Array.isArray(chainSlots) ? chainSlots : []).some(
          (s) => s?.teacher?.id != null && s.teacher.id === currentTeacherId
        );
      }
      return false;
    },
    [isStudent, canConvertAny, canConvert, canConvertOwn, currentTeacherId]
  );

  /**
   * Whether the "Revert" action should show on a converted slot.
   * - Students: never.
   * - Requires isConverted + conversionOverrideId.
   * - `timetable.convert`: any conversion.
   * - `timetable.convert_own` only: allow when the slot carries created-by info
   *   matching the current teacher; otherwise best-effort (teacher teaches the
   *   converted slot) and rely on the backend 403 as the hard gate.
   */
  const canRevertConversion = useCallback(
    (slot) => {
      if (isStudent || !canConvertAny) return false;
      if (!slot?.isConverted || slot?.conversionOverrideId == null) return false;
      if (canConvert) return true;
      if (canConvertOwn && currentTeacherId) {
        const createdBy =
          slot.createdByTeacherId ??
          slot.conversionCreatedByTeacherId ??
          slot.createdByTeacher?.id ??
          null;
        if (createdBy != null) return createdBy === currentTeacherId;
        // Created-by unknown on the slot → best-effort: allow when this teacher
        // teaches the converted slot; backend DELETE enforces the real rule.
        return slot.teacher?.id != null && slot.teacher.id === currentTeacherId;
      }
      return false;
    },
    [isStudent, canConvertAny, canConvert, canConvertOwn, currentTeacherId]
  );

  // Derive classes and schedule from response
  // Use classListData (from separate query) as primary source for dropdown,
  // fall back to classList from the main schedule response
  const classList = classListData || scheduleData?.classList || [];
  const departmentList = scheduleData?.departmentList || [];
  const classes = scheduleData?.classes || [];
  const departmentName = scheduleData?.department?.name || "Department";
  const scheduleDate = scheduleData?.date || "";
  const scheduleWarning = scheduleData?.warning || "";

  // Loading state
  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Department Schedule
        </Typography>
        <ScheduleSkeleton />
      </Box>
    );
  }

  // Error state — show header + dropdown so user can change class selection
  if (isError) {
    const errorMessage = error?.response?.data?.message ||
      error?.message ||
      "Failed to load department schedule. Please try again.";
    // Don't return early — fall through to the main render with error shown inline
  }

  // Get the error message for inline display (null if no error)
  const inlineError = isError
    ? (error?.response?.data?.message || error?.message || "Failed to load department schedule.")
    : null;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h5">Department Schedule</Typography>
          <Typography variant="body2" color="text.secondary">
            {departmentName}
            {scheduleDate ? ` — ${scheduleDate}` : ""}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          {/* Department filter dropdown (visible for super admin or when departmentList is available) */}
          {(isSuperAdmin || departmentList.length > 0) && (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="dept-filter-label">Department</InputLabel>
              <Select
                labelId="dept-filter-label"
                value={selectedDepartmentId}
                onChange={(e) => {
                  setSelectedDepartmentId(e.target.value);
                  setSelectedClassId("");
                }}
                label="Department"
              >
                {!isSuperAdmin && (
                  <MenuItem value="">
                    <em>My Department</em>
                  </MenuItem>
                )}
                {departmentList.map((dept) => (
                  <MenuItem key={dept.id} value={dept.id}>
                    {dept.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {/* Date picker — select any date (past/today/future) to view & edit lectures */}
          <DatePicker
            label="Date"
            format={DATE_DISPLAY_FORMAT}
            value={selectedDate}
            onChange={(date) => {
              if (date && !Number.isNaN(new Date(date).getTime())) {
                setSelectedDate(startOfDay(date));
              }
            }}
            minDate={pickerMinDate}
            maxDate={pickerMaxDate}
            slotProps={{
              textField: { size: "small", sx: { minWidth: 160 } },
            }}
          />
          {/* Class filter dropdown */}
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="class-filter-label">Filter by Class</InputLabel>
            <Select
              labelId="class-filter-label"
              value={selectedClassId}
              onChange={handleClassChange}
              label="Filter by Class"
            >
              <MenuItem value="">
                <em>All Classes</em>
              </MenuItem>
              {classList.map((cls) => (
                <MenuItem key={cls.id} value={cls.id}>
                  {cls.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title="Refresh">
            <IconButton onClick={() => refetch()} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Error state (inline — keeps dropdown visible) */}
      {inlineError && (
        <Alert
          severity="error"
          sx={{ mt: 2, mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          {inlineError}
        </Alert>
      )}

      {/* Warning from backend (e.g., "Selected class has no active timetable") */}
      {!inlineError && scheduleWarning && (
        <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
          {scheduleWarning}
        </Alert>
      )}

      {/* Empty state */}
      {!inlineError && !scheduleWarning && classes.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No schedule found for {selectedClassId ? "the selected class" : "your department"}
          {scheduleDate ? ` on ${scheduleDate}` : ""}.
        </Alert>
      )}

      {/* Schedule cards grouped by class */}
      {classes.map((classGroup) => {
        const slots = Array.isArray(classGroup?.slots) ? classGroup.slots : [];

        // Detect lab blocks + consecutive theory chains once per class group.
        const labBlocks = detectLabBlocks(slots);
        const theoryChains = detectConsecutiveTheoryChains(slots);

        // Map an ANCHOR slot id -> the block/chain it heads, so the block/chain
        // level convert action renders exactly once (on the first slot).
        //
        // UX choice: the "Convert to Theory" action shows on the first batch slot
        // of each lab block (a multi-batch block is offered as a whole; a
        // single-batch lab is its own one-item block). The "Convert to Lab"
        // action shows on the first slot of each theory chain; when a chain has
        // 2+ consecutive theory slots the WHOLE chain is passed as sourceSlots
        // (so the user converts the full consecutive run in one action). A lone
        // theory slot is a length-1 chain and converts just that slot.
        const labBlockByAnchor = new Map();
        for (const block of labBlocks) {
          const anchor = block.slots?.[0];
          if (anchor?.id != null) labBlockByAnchor.set(anchor.id, block);
        }
        const theoryChainByAnchor = new Map();
        for (const chain of theoryChains) {
          const anchor = chain?.[0];
          if (anchor?.id != null) theoryChainByAnchor.set(anchor.id, chain);
        }

        return (
        <Card key={classGroup.classId} sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {classGroup.className}
            </Typography>

            {slots.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No slots scheduled{scheduleDate ? ` on ${scheduleDate}` : ""}.
              </Typography>
            )}

            {slots.map((slot) => {
              const chipProps = getOverrideChipProps(slot);

              // Block/chain this slot heads (undefined when not an anchor).
              const labBlock =
                slot?.id != null ? labBlockByAnchor.get(slot.id) : undefined;
              const theoryChain =
                slot?.id != null ? theoryChainByAnchor.get(slot.id) : undefined;

              const showConvertToTheory =
                Boolean(labBlock) && canConvertLabBlock(labBlock.slots);
              const showConvertToLab =
                Boolean(theoryChain) && canConvertTheory(theoryChain);
              const showConvertRevert = canRevertConversion(slot);

              return (
                <Card
                  key={slot.id}
                  variant="outlined"
                  sx={{
                    mb: 1.5,
                    cursor: slot.isEdited ? "pointer" : "default",
                    "&:hover": slot.isEdited
                      ? { borderColor: "secondary.main", bgcolor: "action.hover" }
                      : {},
                    opacity: slot.isCancelledByExtend ? 0.6 : 1,
                  }}
                  onClick={() => handleSlotClick(slot)}
                >
                  <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        flexWrap: "wrap",
                        gap: 1,
                      }}
                    >
                      {/* Left: slot info */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                          <TimeIcon fontSize="small" color="action" />
                          <Typography variant="body2" fontWeight={500}>
                            {formatTime12h(slot.startTime)} – {formatTime12h(slot.endTime)}
                          </Typography>
                          {slot.isBreak && (
                            <Chip label="Break" size="small" variant="outlined" />
                          )}
                          {chipProps && (
                            <Chip
                              label={chipProps.label}
                              color={chipProps.color}
                              size="small"
                            />
                          )}
                        </Box>

                        {!slot.isBreak && (
                          <>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.3 }}>
                              <PersonIcon fontSize="small" color="action" />
                              <Typography variant="body2">
                                {slot.teacher?.name || "—"}
                              </Typography>
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.3 }}>
                              <SubjectIcon fontSize="small" color="action" />
                              <Typography variant="body2">
                                {slot.subject?.name || "—"}
                              </Typography>
                            </Box>
                            {slot.room && (
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <RoomIcon fontSize="small" color="action" />
                                <Typography variant="body2">{slot.room}</Typography>
                              </Box>
                            )}
                          </>
                        )}
                      </Box>

                      {/* Right: actions */}
                      {!slot.isBreak && !slot.isCancelledByExtend && !isStudent && (
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          {/* Edit Lecture button — permission-gated (edit_lecture) */}
                          {canEditSlot(slot) && (
                            <Tooltip title="Edit Lecture">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={(e) => handleEditClick(e, slot, classGroup)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {/* Revert button — visible for users with override OR own_override for own slot */}
                          {canRevertSlot(slot) && (
                            <Tooltip title="Revert Override">
                              <IconButton
                                size="small"
                                color="warning"
                                onClick={(e) => handleRevertClick(e, slot)}
                              >
                                <RevertIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {/* Convert to Theory — on the anchor of a lab block */}
                          {showConvertToTheory && (
                            <Tooltip title="Convert to Theory">
                              <IconButton
                                size="small"
                                color="secondary"
                                onClick={(e) =>
                                  handleConvertToTheory(e, labBlock.slots, classGroup)
                                }
                              >
                                <ConvertIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {/* Convert to Lab — on the anchor of a theory chain */}
                          {showConvertToLab && (
                            <Tooltip
                              title={
                                theoryChain.length > 1
                                  ? `Convert to Lab (${theoryChain.length} consecutive lectures)`
                                  : "Convert to Lab"
                              }
                            >
                              <IconButton
                                size="small"
                                color="secondary"
                                onClick={(e) =>
                                  handleConvertToLab(e, theoryChain, classGroup)
                                }
                              >
                                <LabIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {/* Revert Conversion — on a converted virtual slot */}
                          {showConvertRevert && (
                            <Tooltip title="Revert Conversion">
                              <IconButton
                                size="small"
                                color="warning"
                                onClick={(e) => handleConvertRevertClick(e, slot)}
                              >
                                <RevertIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </CardContent>
        </Card>
        );
      })}

      {/* Slot detail dialog */}
      <SlotDetailDialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        slot={detailSlot}
      />

      {/* Revert confirmation dialog */}
      <Dialog
        open={revertConfirmOpen}
        onClose={() => setRevertConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Revert Override?</DialogTitle>
        <DialogContent>
          <Typography>
            This will remove the edit override and restore the original schedule for this slot.
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevertConfirmOpen(false)} disabled={revertMutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleRevertConfirm}
            color="warning"
            variant="contained"
            disabled={revertMutation.isPending}
          >
            {revertMutation.isPending ? "Reverting..." : "Revert"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Conversion revert confirmation dialog (mirrors edit revert dialog) */}
      <Dialog
        open={convertRevertOpen}
        onClose={() => setConvertRevertOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Revert Conversion?</DialogTitle>
        <DialogContent>
          <Typography>
            This will remove the lecture conversion and restore the original
            schedule for this slot. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConvertRevertOpen(false)}
            disabled={conversionRevertMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConvertRevertConfirm}
            color="warning"
            variant="contained"
            disabled={conversionRevertMutation.isPending}
          >
            {conversionRevertMutation.isPending ? "Reverting..." : "Revert"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Lecture Form Modal */}
      <EditLectureForm
        open={editFormOpen}
        onClose={handleEditFormClose}
        slot={editFormSlot}
        date={selectedDate}
        onSuccess={handleEditFormSuccess}
      />

      {/* Convert Lab → Theory modal */}
      <ConvertLabToTheoryModal
        open={labToTheoryOpen}
        onClose={() => setLabToTheoryOpen(false)}
        sourceSlots={labToTheorySources}
        classId={conversionContext.classId}
        className={conversionContext.className}
        date={selectedDate || scheduleDate || new Date()}
        onSuccess={handleConversionSuccess}
      />

      {/* Convert Theory → Lab modal */}
      <ConvertTheoryToLabModal
        open={theoryToLabOpen}
        onClose={() => setTheoryToLabOpen(false)}
        sourceSlots={theoryToLabSources}
        classId={conversionContext.classId}
        className={conversionContext.className}
        date={selectedDate || scheduleDate || new Date()}
        onSuccess={handleConversionSuccess}
      />
    </Box>
  );
};

export default DepartmentSchedule;
